import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutList, Plus, AlertTriangle, PackageX } from "lucide-react";
import { RequestCard } from "../components/cards";
import { Spinner, EmptyState } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import api, { apiError } from "../lib/api";
import { rupiah } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

export default function RequestBoard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reqs, setReqs] = useState([]);
  const [dests, setDests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ destination: "", product_name: "", description: "", budget: "", size: "", deadline: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = () => { setLoading(true); api.get("/requests/board").then((r) => setReqs(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); api.get("/destinations").then((r) => setDests(r.data)); }, []);

  const postReq = async (e) => {
    e.preventDefault();
    if (!form.destination || !form.product_name || !form.budget) return toast.error("Lengkapi destinasi, produk & budget");
    setBusy(true);
    try {
      await api.post("/requests", { type: "board", ...form, quantity: 1, budget: Number(form.budget) });
      toast.success("Request diposting ke papan!");
      setOpen(false); setForm({ destination: "", product_name: "", description: "", budget: "", size: "", deadline: "" }); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const canBring = async (req) => {
    try {
      const { data } = await api.post(`/requests/${req.id}/can-bring`);
      toast.success("Kamu terhubung dengan " + req.titiper?.name);
      navigate(`/chat/${data.conversation_id}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-extrabold text-2xl text-navy flex items-center gap-2"><LayoutList className="text-sky" /> {t("nav_board")}</h1>
          <p className="text-muted-foreground text-sm mt-1">TITIPER posting apa yang mereka cari. BAWAINER bisa menawarkan bantuan.</p>
        </div>
        {user?.role === "titiper" && (
          <button onClick={() => setOpen(true)} data-testid="open-post-request"
            className="rounded-full bg-lime hover:bg-lime-dark text-navy font-bold px-4 py-2.5 text-sm flex items-center gap-1.5 transition-colors">
            <Plus size={16} /> {t("post_request")}
          </button>
        )}
      </div>

      {loading ? <Spinner /> : reqs.length === 0 ? (
        <EmptyState icon={PackageX} title="Belum ada request" subtitle="Jadilah yang pertama posting request barang." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {reqs.map((r) => (
            <RequestCard key={r.id} req={r}
              action={user?.role === "bawainer" ? (
                <button onClick={() => canBring(r)} data-testid={`can-bring-${r.id}`}
                  className="mt-3 w-full rounded-full bg-navy text-white font-bold py-2.5 text-sm hover:bg-navy-700 transition-colors">
                  {t("i_can_bring")}
                </button>
              ) : (
                <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
                  <img src={r.titiper?.avatar} alt="" className="w-5 h-5 rounded-full" /> {r.titiper?.name}
                </div>
              )} />
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="post-request-dialog">
          <DialogHeader><DialogTitle className="font-heading text-navy">{t("post_request")}</DialogTitle></DialogHeader>
          <form onSubmit={postReq} className="space-y-3">
            <div><label className="text-sm font-semibold text-ink">Destinasi</label>
              <select data-testid="board-dest" value={form.destination} onChange={set("destination")}
                className="w-full h-9 rounded-xl border border-input px-3 text-sm mt-1 bg-transparent">
                <option value="">Pilih negara/kota</option>
                {dests.map((d) => <option key={d.code} value={d.name}>{d.flag} {d.name}</option>)}
              </select></div>
            <div><label className="text-sm font-semibold text-ink">Produk</label>
              <Input data-testid="board-product" value={form.product_name} onChange={set("product_name")} className="rounded-xl mt-1" placeholder="Onitsuka Tiger" /></div>
            <div><label className="text-sm font-semibold text-ink">Deskripsi</label>
              <Input data-testid="board-desc" value={form.description} onChange={set("description")} className="rounded-xl mt-1" placeholder="Warna, ukuran, model" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-semibold text-ink">Size</label>
                <Input data-testid="board-size" value={form.size} onChange={set("size")} className="rounded-xl mt-1" placeholder="42" /></div>
              <div><label className="text-sm font-semibold text-ink">Deadline</label>
                <Input data-testid="board-deadline" type="date" value={form.deadline} onChange={set("deadline")} className="rounded-xl mt-1" /></div>
            </div>
            <div><label className="text-sm font-semibold text-ink">Budget (Rp)</label>
              <Input data-testid="board-budget" type="number" value={form.budget} onChange={set("budget")} className="rounded-xl mt-1" placeholder="2500000" />
              {form.budget ? <p className="text-xs text-muted-foreground mt-1">{rupiah(form.budget)}</p> : null}</div>
            <div className="flex gap-2 items-start bg-orange-50 rounded-xl p-3 text-xs text-orange-700">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" /> Pastikan barang legal untuk dibawa & sesuai regulasi negara asal dan tujuan.
            </div>
            <button type="submit" disabled={busy} data-testid="board-submit"
              className="w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-3 transition-colors disabled:opacity-60">
              {busy ? "..." : t("post_request")}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
